import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import { useUpdateSessionMetadata } from '@utils/api/sessions/use-update-session-metadata.tsx';
import { useRegenerateWaveInfo } from '@utils/api/sessions/use-regenerate-wave-info.tsx';
import { useSearchReplacementSessions } from '@utils/api/sessions/use-search-replacement-sessions.tsx';
import { useDebouncedValue } from '@utils/hooks/use-debounced-value.ts';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { Session } from '@utils/types/editors/sessions.ts';
import { waveInfoErrorKey } from './wave-info-error.ts';
import { WaveInfoNotices } from './wave-info-notices.tsx';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  currentMediaUrl?: string | null;
  session?: Session;
};

type Option = { label: string; value: string };

const isValidHttpUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const isInternalBlobUrl = (value: string | null | undefined): boolean => {
  if (!value) return false;
  try {
    const hostname = new URL(value).hostname;
    return (
      hostname.endsWith('blob.scriptix.dev') ||
      hostname.endsWith('blob.scriptix.app') ||
      hostname.endsWith('blob.scriptix.io')
    );
  } catch {
    return false;
  }
};

export default function MediaSourceModal({
  isOpen,
  onClose,
  sessionId,
  currentMediaUrl,
  session
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const selectedRefId = session?.media_source_session_id ?? null;
  const isInternal = isInternalBlobUrl(currentMediaUrl);
  const [url, setUrl] = useState(
    isInternal || selectedRefId ? '' : currentMediaUrl || ''
  );
  const [replacement, setReplacement] = useState<Option | null>(
    selectedRefId
      ? {
          value: selectedRefId,
          label: session?.media_source_session_filename || selectedRefId
        }
      : null
  );
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [error, setError] = useState('');

  const { data: candidates = [], isFetching } = useSearchReplacementSessions(
    debouncedSearch,
    sessionId,
    isOpen
  );
  const options = useMemo<Option[]>(
    () =>
      candidates.map(row => ({
        value: row.id,
        label: row.filename || row.id
      })),
    [candidates]
  );

  const waveInfoStatus = session?.wave_info_status ?? null;
  const isRegenInFlight =
    waveInfoStatus === 'pending' || waveInfoStatus === 'running';
  const keepMedia = session?.keep_media ?? false;

  const { mutate: updateSession, isLoading: isSaving } =
    useUpdateSessionMetadata({
      onSuccess: () => {
        toast.success(t('media-source-updated'));
        onClose();
      },
      onError: () => {
        toast.error(t('media-source-update-failed'));
      }
    });

  const { mutate: regenerate, isLoading: isRegenerating } =
    useRegenerateWaveInfo(sessionId);

  const isBusy = isSaving || isRegenerating || isRegenInFlight;
  const replacementChanged =
    replacement !== null && replacement.value !== selectedRefId;

  const handleSave = () => {
    if (replacementChanged && replacement) {
      setError('');
      updateSession({ sessionId, mediaSourceSessionId: replacement.value });
      return;
    }
    const trimmed = url.trim();
    if (!trimmed) {
      if (replacement) {
        onClose();
        return;
      }
      setError(t('media-source-required'));
      return;
    }
    if (!isValidHttpUrl(trimmed)) {
      setError(t('media-source-url-invalid'));
      return;
    }
    setError('');
    updateSession({
      sessionId,
      mediaSource: trimmed,
      mediaSourceSessionId: null
    });
  };

  const handleRemove = () => {
    setError('');
    updateSession({
      sessionId,
      mediaSource: null,
      mediaSourceSessionId: null
    });
  };

  const handleRegenerate = () => {
    setError('');
    regenerate(undefined, {
      onSuccess: result => {
        if (result.status === 'failed') {
          toast.error(t(waveInfoErrorKey(result.error)));
        } else {
          toast.success(t('wave-info-regeneration-queued'));
        }
      },
      onError: () => {
        toast.error(t('wave-info-regeneration-failed'));
      }
    });
  };

  const hasOverride = Boolean(
    selectedRefId || (currentMediaUrl && !isInternal)
  );
  const warning = { color: getColour('normalText'), fontWeight: 500 };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isBusy && onClose()}
      size="medium"
      title={t('media-source')}
      caption={t('media-source-description')}
      footerActions={[
        ...(hasOverride
          ? [
              <Button
                key="remove"
                onClick={handleRemove}
                label={t('remove')}
                size="small"
                variant="smoke"
                disabled={isBusy}
              />
            ]
          : []),
        ...(keepMedia
          ? [
              <Button
                key="regenerate"
                onClick={handleRegenerate}
                label={
                  isRegenerating || isRegenInFlight
                    ? t('regenerating-audio-timeline')
                    : t('regenerate-audio-timeline')
                }
                size="small"
                variant="smoke"
                disabled={isBusy}
              />
            ]
          : []),
        <Button
          key="cancel"
          onClick={onClose}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isBusy}
        />,
        <Button
          key="save"
          onClick={handleSave}
          label={isSaving ? t('saving') : t('save')}
          size="small"
          variant="primary"
          disabled={isBusy}
        />
      ]}
    >
      <div className="py-2">
        <div className="min-w-72 space-y-3">
          <WaveInfoNotices session={session} />

          <SelectInput
            id="media-source-session"
            label={t('media-source-session')}
            options={options}
            value={replacement}
            onChange={(option: Option | null) => {
              setReplacement(option);
              if (option) setUrl('');
              if (error) setError('');
            }}
            onInputChange={setSearch}
            placeholder={
              isFetching ? t('loading') : t('media-source-session-placeholder')
            }
            helperText={t('media-source-session-hint')}
          />

          {isInternal && !selectedRefId && (
            <p className="text-xs" style={warning}>
              {t('media-source-override-note')}
            </p>
          )}
          <Input
            id="media-source-url"
            label={t('media-source-url')}
            value={url}
            onChange={e => {
              setUrl(e.target.value);
              if (e.target.value) setReplacement(null);
              if (error) setError('');
            }}
            type="url"
            placeholder="https://example.com/media.mp3"
            disabled={isBusy}
          />
          {error && (
            <p className="text-xs" style={{ color: getColour('danger') }}>
              {error}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
